import { AdminAttendeeAvatar } from '@/features/photos/PhotoAvatars'
import { useState, useMemo } from 'react'
import { useNavigate } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { adminApi, type AttendeeListItemDto } from '@/api/admin'
import { useTopBar } from '@/contexts/TopBarContext'
import { Search, X, Check, Mail } from 'lucide-react'
import { usePagination } from '@/hooks/usePagination'
import { TablePagination } from '@/components/shared/TablePagination'
import { useAdminDates } from '@/hooks/useDates'

export default function AttendeesListPage() {
  const { formatDate } = useAdminDates()
  const fmtDate = (date: string) => {
    return formatDate(date, { day: 'numeric', month: 'short', year: 'numeric' })
  }
  useTopBar(<span className="text-sm font-medium text-gray-900">Attendees</span>)
  const navigate = useNavigate()
  const [search, setSearch] = useState('')

  const { data: attendees = [], isLoading } = useQuery({
    queryKey: ['attendees'],
    queryFn: () => adminApi.getAttendees().then(r => r.data),
  })

  const filtered = useMemo(() => {
    const q = search.toLowerCase().trim()
    if (!q) return attendees
    return attendees.filter(a =>
      [a.fullName, a.email, a.companyName ?? ''].some(v => v.toLowerCase().includes(q)))
  }, [attendees, search])

  const { paged, page, setPage, totalPages, total, pageSize, start } = usePagination(filtered)

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <p className="text-sm text-gray-500">People who have booked onto your public sessions.</p>
        <span className="text-sm text-gray-400">{filtered.length} of {attendees.length}</span>
      </div>

      {/* Search */}
      <div className="relative mb-4 max-w-sm">
        <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" />
        <input
          value={search}
          onChange={e => setSearch(e.target.value)}
          placeholder="Search by name, email, company…"
          className="w-full pl-9 pr-8 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
        />
        {search && (
          <button onClick={() => setSearch('')} className="absolute right-2.5 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 cursor-pointer">
            <X size={14} />
          </button>
        )}
      </div>

      {/* Mobile card list */}
      <div className="sm:hidden space-y-2">
        {isLoading && <p className="text-center text-gray-500 py-8">Loading…</p>}
        {!isLoading && filtered.length === 0 && (
          <p className="text-center text-gray-400 py-8">
            {attendees.length === 0 ? 'No attendees yet. They appear here once someone books a public session.' : 'No attendees match your search.'}
          </p>
        )}
        {paged.map((a: AttendeeListItemDto) => (
          <div
            key={a.id}
            onClick={() => navigate(`/admin/attendees/${a.id}`)}
            className="bg-white border border-gray-200 rounded-lg p-4 cursor-pointer hover:border-indigo-200"
          >
            <div className="flex items-start justify-between gap-2">
              <div className="min-w-0 flex-1 flex items-center gap-2.5">
                <AdminAttendeeAvatar attendee={a} size={36} />
                <div className="min-w-0">
                  <p className="font-medium text-gray-900">{a.fullName}</p>
                  <p className="text-xs text-gray-400 mt-0.5 truncate">{a.email}</p>
                </div>
              </div>
              {a.marketingConsent && <Check size={14} className="text-green-500 shrink-0 mt-0.5" />}
            </div>
            <div className="mt-2 flex flex-wrap gap-x-4 gap-y-1 text-xs text-gray-500">
              {a.companyName && <span>{a.companyName}</span>}
              {a.clientStaffOf && <span className="rounded-full bg-indigo-50 px-2 py-0.5 text-indigo-700">Client staff: {a.clientStaffOf}</span>}
              <span>{a.sessionsCount} sessions</span>
              {a.certificatesCount > 0 && <span>{a.certificatesCount} certs</span>}
              {a.lastBookingAt && <span>Last: {fmtDate(a.lastBookingAt)}</span>}
            </div>
          </div>
        ))}
        <TablePagination page={page} totalPages={totalPages} total={total} pageSize={pageSize} start={start} onPage={setPage} />
      </div>

      {/* Desktop table */}
      <div className="hidden sm:block bg-white border border-gray-200 rounded-lg overflow-hidden shadow-sm">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Name</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Company</th>
                <th className="px-4 py-3 text-right text-xs font-semibold text-gray-500 uppercase tracking-wider">Sessions</th>
                <th className="px-4 py-3 text-right text-xs font-semibold text-gray-500 uppercase tracking-wider">Certificates</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Last booking</th>
                <th className="px-4 py-3 text-center text-xs font-semibold text-gray-500 uppercase tracking-wider">Marketing</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {isLoading && (
                <tr><td colSpan={6} className="text-center text-gray-500 py-12">Loading…</td></tr>
              )}
              {!isLoading && filtered.length === 0 && (
                <tr><td colSpan={6} className="text-center text-gray-400 py-12">
                  {attendees.length === 0 ? 'No attendees yet. They appear here once someone books a public session.' : 'No attendees match your search.'}
                </td></tr>
              )}
              {paged.map((a: AttendeeListItemDto) => (
                <tr
                  key={a.id}
                  className="transition-colors cursor-pointer hover:bg-indigo-50/40"
                  onClick={() => navigate(`/admin/attendees/${a.id}`)}
                >
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-2.5">
                      <AdminAttendeeAvatar attendee={a} />
                      <div className="min-w-0">
                        <p className="font-medium text-gray-900">{a.fullName}</p>
                        <p className="text-xs text-gray-400 mt-0.5 truncate max-w-56">{a.email}</p>
                      </div>
                    </div>
                  </td>
                  <td className="px-4 py-3 text-gray-500 whitespace-nowrap">
                    {a.companyName ?? <span className="text-gray-300">—</span>}
                    {a.clientStaffOf && <span className="ml-2 rounded-full bg-indigo-50 px-2 py-0.5 text-xs text-indigo-700">Client staff</span>}
                  </td>
                  <td className="px-4 py-3 text-right tabular-nums text-gray-700">{a.sessionsCount}</td>
                  <td className="px-4 py-3 text-right tabular-nums text-gray-700">{a.certificatesCount}</td>
                  <td className="px-4 py-3 text-gray-500 whitespace-nowrap">
                    {a.lastBookingAt ? fmtDate(a.lastBookingAt) : <span className="text-gray-300">—</span>}
                  </td>
                  <td className="px-4 py-3 text-center">
                    {a.marketingConsent
                      ? <Check size={15} className="inline text-green-600" />
                      : <Mail size={14} className="inline text-gray-300" />}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <TablePagination page={page} totalPages={totalPages} total={total} pageSize={pageSize} start={start} onPage={setPage} />
      </div>
    </div>
  )
}
